import { morphSection } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';
import { warrantyPairToken } from '@theme/warranty-pair';
import {
  clearCartRemoveUndoQueue,
  flushCartRemoveUndo,
  normalizeCartLineProperties,
} from './component-cart-items.js';

const AUTO_HIDE_MS = 5000;

/**
 * @typedef {import('./component-cart-items.js').CartRemoveUndoPayload} CartRemoveUndoPayload
 */

class CartRemoveUndo extends HTMLElement {
  /** @type {CartRemoveUndoPayload | null} */
  #payload = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #hideTimer = null;

  connectedCallback() {
    const existing = document.querySelector('cart-remove-undo');
    if (existing && existing !== this) {
      if (existing.classList.contains('is-visible')) {
        this.remove();
        return;
      }
      existing.remove();
    }

    const isInsideDialog = Boolean(this.closest('dialog'));

    if (this.parentElement !== document.body && !isInsideDialog) {
      document.body.appendChild(this);
    }

    this.refs.closeButton?.addEventListener('click', this.hide);
    this.refs.undoButton?.addEventListener('click', this.#undo);
    flushCartRemoveUndo();
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.refs.closeButton?.removeEventListener('click', this.hide);
    this.refs.undoButton?.removeEventListener('click', this.#undo);
  }

  /**
   * @param {CartRemoveUndoPayload} payload
   */
  show(payload) {
    this.#payload = payload;

    // PP-987: the message names the warranty when it was removed with the line.
    const template =
      (payload.warrantyVariantId ? this.dataset.messageTemplateWarranty : '') ||
      this.dataset.messageTemplate ||
      '';
    const title = payload.title ?? '';
    const message = template.replace('__TITLE__', title).replace(/\{\{\s*title\s*\}\}/gi, title);

    if (this.refs.message) {
      this.refs.message.textContent = message;
    }

    this.hidden = false;
    this.classList.add('is-visible');
    this.#scheduleHide();
  }

  hide = () => {
    this.#clearTimer();
    this.#payload = null;
    clearCartRemoveUndoQueue();
    this.hidden = true;
    this.classList.remove('is-visible');
  };

  #scheduleHide() {
    this.#clearTimer();
    this.#hideTimer = setTimeout(this.hide, AUTO_HIDE_MS);
  }

  #clearTimer() {
    if (this.#hideTimer) {
      clearTimeout(this.#hideTimer);
      this.#hideTimer = null;
    }
  }

  #undo = async () => {
    const payload = this.#payload;
    if (!payload) return;

    this.hide();

    /** @type {{ id: number; quantity: number; properties?: Record<string, string>; selling_plan?: number }} */
    const lineItem = {
      id: payload.variantId,
      quantity: payload.quantity,
    };

    const properties = normalizeCartLineProperties(payload.properties);
    if (Object.keys(properties).length > 0) {
      lineItem.properties = properties;
    }

    if (payload.sellingPlan) {
      lineItem.selling_plan = payload.sellingPlan;
    }

    const items = [lineItem];
    if (payload.warrantyVariantId) {
      // PP-987: the line was removed together with its warranty, so the undo
      // brings both back as a pair. Same pairing as an add-to-cart with a picked
      // warranty (product-form.js): the product line carries the visible SKU
      // marker and the hidden pair token, the warranty line the same token.
      const pairId = warrantyPairToken(payload.variantId, payload.warrantyVariantId, properties);
      lineItem.properties = {
        ...(lineItem.properties ?? {}),
        warrantySKU: payload.warrantySku ?? '',
        _warranty_pair_id: pairId,
      };
      items.push({
        id: payload.warrantyVariantId,
        quantity: payload.quantity,
        properties: { _warranty: 'true', _warranty_pair_id: pairId },
      });
    }

    const sections = this.#getSectionsToUpdate();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: { order_type: 'online' },
          items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        console.error(data.description || data.message);
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'cart-remove-undo',
          itemCount: data.item_count,
          sections: data.sections,
          variantId: String(payload.variantId),
        })
      );

      for (const sectionId of sections) {
        const sectionHtml = data.sections?.[sectionId];
        if (sectionHtml) {
          morphSection(sectionId, sectionHtml, 'full');
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  /** @returns {string[]} */
  #getSectionsToUpdate() {
    /** @type {Set<string>} */
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    if (this.#payload?.sectionId) {
      sectionIds.add(this.#payload.sectionId);
    }

    return Array.from(sectionIds);
  }

  /** @type {Record<string, HTMLElement | undefined>} */
  get refs() {
    return {
      message: this.querySelector('[ref="message"]') ?? undefined,
      undoButton: this.querySelector('[ref="undoButton"]') ?? undefined,
      closeButton: this.querySelector('[ref="closeButton"]') ?? undefined,
    };
  }
}

if (!customElements.get('cart-remove-undo')) {
  customElements.define('cart-remove-undo', CartRemoveUndo);
}
